import { Button } from "@/components/ui/button";
import { useNavigate, Link } from "react-router-dom";
import { Menu, User, Settings, LogOut } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useState, useCallback } from "react";
import Logo from "@/components/Logo";
import { useAuth } from "@/hooks/useAuth";
import { useProfileQuery } from "@/hooks/useProfileQuery";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const Navigation = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, signOut } = useAuth();
  
  // Use shared profile query - only fetches when user exists
  const { data: profile } = useProfileQuery();

  // Prefetch dashboard data on hover for faster navigation
  const handleDashboardHover = useCallback(() => {
    if (user) {
      queryClient.prefetchQuery({
        queryKey: ["feedback-pages", user.id],
        queryFn: async () => {
          const { data } = await supabase
            .from("feedback_pages")
            .select("*")
            .order("created_at", { ascending: false });
          return data || [];
        },
        staleTime: 60000,
      });
    }
  }, [user, queryClient]);

  const getUserInitials = useCallback(() => {
    if (profile?.company_name) {
      const names = profile.company_name.split(' ');
      return names.map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return user?.email?.[0].toUpperCase() || 'U';
  }, [profile?.company_name, user?.email]);

  const handleSmoothScroll = useCallback((e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    const target = document.getElementById(targetId);
    if (target) {
      e.preventDefault();
      setMobileMenuOpen(false);
      setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, []);

  return (
    <nav className="border-b border-border bg-background/95 backdrop-blur-sm sticky top-0 z-50">
      <div className="container mx-auto px-4 md:px-6 py-3 md:py-4">
        <div className="flex items-center justify-between relative">
          <Link to="/" className="cursor-pointer flex items-center h-9">
            <Logo size="md" />
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8 text-sm absolute left-1/2 -translate-x-1/2 h-9">
            <a 
              href="/#features" 
              className="text-muted-foreground hover:text-foreground transition-colors font-medium flex items-center h-9"
              onClick={(e) => handleSmoothScroll(e, 'features')}
            >
              Features
            </a>
            <a 
              href="/#how-it-works" 
              className="text-muted-foreground hover:text-foreground transition-colors font-medium flex items-center h-9"
              onClick={(e) => handleSmoothScroll(e, 'how-it-works')}
            >
              How it works
            </a>
            <Link to="/pricing" className="text-muted-foreground hover:text-foreground transition-colors font-medium flex items-center h-9">
              Pricing
            </Link>
            <a 
              href="/#faq" 
              className="text-muted-foreground hover:text-foreground transition-colors font-medium flex items-center h-9"
              onClick={(e) => handleSmoothScroll(e, 'faq')}
            >
              FAQ
            </a>
          </div>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center gap-3 h-9">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-2 h-9">
                    <Avatar className="h-7 w-7 rounded-lg">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs rounded-lg">
                        {getUserInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium">{profile?.company_name || user.email}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-background z-50">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{profile?.company_name || 'My Account'}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => navigate('/dashboard')} 
                    onMouseEnter={handleDashboardHover}
                    className="cursor-pointer"
                  >
                    <User className="mr-2 h-4 w-4" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/dashboard/settings?tab=account')} className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    Account
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/dashboard/settings')} className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={signOut} className="cursor-pointer text-destructive focus:text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="font-medium h-9"
                  onClick={() => navigate('/auth')}
                >
                  Sign In
                </Button>
                <Button 
                  variant="default" 
                  size="sm" 
                  className="font-medium h-9"
                  onClick={() => navigate('/auth?mode=signup')}
                >
                  Sign Up
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className="md:hidden h-9 w-9">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[320px]">
              <div className="flex flex-col gap-6 mt-8">
                <a 
                  href="/#features" 
                  className="text-base font-medium text-foreground hover:text-primary transition-colors py-2"
                  onClick={(e) => handleSmoothScroll(e, 'features')}
                >
                  Features
                </a>
                <a 
                  href="/#how-it-works" 
                  className="text-base font-medium text-foreground hover:text-primary transition-colors py-2"
                  onClick={(e) => handleSmoothScroll(e, 'how-it-works')}
                >
                  How it works
                </a>
                <Link 
                  to="/pricing" 
                  className="text-base font-medium text-foreground hover:text-primary transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Pricing
                </Link>
                <a 
                  href="/#faq" 
                  className="text-base font-medium text-foreground hover:text-primary transition-colors py-2"
                  onClick={(e) => handleSmoothScroll(e, 'faq')}
                >
                  FAQ
                </a>
                
                <div className="pt-4 border-t flex flex-col gap-3">
                  {user ? (
                    <>
                      <div className="flex items-center gap-3 px-2 py-3">
                        <Avatar className="h-10 w-10 rounded-lg">
                          <AvatarFallback className="bg-primary text-primary-foreground text-sm rounded-lg">
                            {getUserInitials()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col">
                          <p className="text-sm font-medium">{profile?.company_name || 'My Account'}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start h-11"
                        onClick={() => {
                          navigate('/dashboard');
                          setMobileMenuOpen(false);
                        }}
                      >
                        <User className="mr-2 h-4 w-4" />
                        Dashboard
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start h-11"
                        onClick={() => {
                          navigate('/dashboard/settings?tab=account');
                          setMobileMenuOpen(false);
                        }}
                      >
                        <User className="mr-2 h-4 w-4" />
                        Account
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start h-11"
                        onClick={() => {
                          navigate('/dashboard/settings');
                          setMobileMenuOpen(false);
                        }}
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        Settings
                      </Button>
                      <Button 
                        variant="destructive" 
                        className="w-full justify-start h-11"
                        onClick={() => {
                          signOut();
                          setMobileMenuOpen(false);
                        }}
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        Sign Out
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button 
                        variant="outline" 
                        className="w-full justify-center h-11"
                        onClick={() => {
                          navigate('/auth');
                          setMobileMenuOpen(false);
                        }}
                      >
                        Sign In
                      </Button>
                      <Button 
                        variant="default" 
                        className="w-full justify-center h-11"
                        onClick={() => {
                          navigate('/auth?mode=signup');
                          setMobileMenuOpen(false);
                        }}
                      >
                        Sign Up
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
